import { useEffect, useRef, useState } from 'react';
import { heicWorkerManager } from '../utils/heicWorkerManager';
import { getCacheKey, getCachedImage, setCachedImage } from '../utils/heicCache';

// Detect a HEIC/HEIF image from its URL (ignoring any query string), e.g. photos
// uploaded from an iPhone. Browsers other than Safari can't decode these, so they
// must be converted to JPEG before being handed to an <img>/antd Image.
export const isHeicUrl = (url: string): boolean => {
	if (!url) return false;
	const path = url.toLowerCase().split('?')[0];
	return path.endsWith('.heic') || path.endsWith('.heif');
};

// Deduplicate concurrent conversions of the same URL across all hook instances.
const inFlightConversions = new Map<string, Promise<Blob>>();

const convertHeicUrlToJpeg = async (url: string): Promise<Blob> => {
	const cacheKey = getCacheKey(url);

	const cachedBlob = await getCachedImage(cacheKey);
	if (cachedBlob) {
		return cachedBlob;
	}

	const inFlight = inFlightConversions.get(cacheKey);
	if (inFlight) {
		return inFlight;
	}

	const conversionPromise = (async () => {
		try {
			const response = await fetch(url);
			// fetch only rejects on a network failure, so a 403/404 would otherwise reach
			// heic2any as an error body and surface as a decode failure.
			if (!response.ok) {
				throw new Error(`HEIC fetch failed with status ${response.status}`);
			}
			const blob = await response.blob();
			const arrayBuffer = await blob.arrayBuffer();
			const jpegBlob = await heicWorkerManager.convert(arrayBuffer);
			setCachedImage(cacheKey, jpegBlob).catch(console.error);
			return jpegBlob;
		} finally {
			inFlightConversions.delete(cacheKey);
		}
	})();

	inFlightConversions.set(cacheKey, conversionPromise);
	return conversionPromise;
};

export interface HeicSafeSrc {
	/** The URL to render. Empty only while a conversion is in flight. */
	src: string;
	/** True exactly while this URL is being decoded to JPEG in the browser. */
	isConverting: boolean;
	/** True when the decode failed and `src` fell back to the undecodable original. */
	hasError: boolean;
}

/**
 * Resolve a full image URL to one a browser can actually render. A `.heic`/`.heif`
 * URL is fetched and decoded to JPEG on the main thread (via `heicWorkerManager`,
 * which despite its name dynamically imports heic2any rather than running a Web
 * Worker) plus an IndexedDB cache, and returned as an object URL; every other URL
 * is returned unchanged. While a conversion is in flight `src` is empty and
 * `isConverting` is true, so a caller can render a placeholder instead of a blank
 * box. The object URL is revoked on unmount / URL change so blobs aren't leaked.
 * A failed conversion falls back to the original URL with `hasError` set.
 */
export const useHeicSafeSrc = (url: string): HeicSafeSrc => {
	const [state, setState] = useState<HeicSafeSrc>(() =>
		isHeicUrl(url)
			? { src: '', isConverting: true, hasError: false }
			: { src: url, isConverting: false, hasError: false }
	);
	const objectUrlRef = useRef<string | null>(null);

	useEffect(() => {
		let cancelled = false;

		const revokeObjectUrl = () => {
			if (objectUrlRef.current) {
				URL.revokeObjectURL(objectUrlRef.current);
				objectUrlRef.current = null;
			}
		};

		if (!isHeicUrl(url)) {
			revokeObjectUrl();
			setState({ src: url, isConverting: false, hasError: false });
			return;
		}

		setState({ src: '', isConverting: true, hasError: false });
		convertHeicUrlToJpeg(url)
			.then((jpegBlob) => {
				if (cancelled) return;
				const objectUrl = URL.createObjectURL(jpegBlob);
				objectUrlRef.current = objectUrl;
				setState({ src: objectUrl, isConverting: false, hasError: false });
			})
			.catch(() => {
				if (cancelled) return;
				// Conversion failed — fall back to the original URL rather than showing nothing.
				setState({ src: url, isConverting: false, hasError: true });
			});

		return () => {
			cancelled = true;
			revokeObjectUrl();
		};
	}, [url]);

	return state;
};
